import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';

import { SCROLL_DEPTH_THRESHOLD_PERCENT } from '../../shared/constants';

/**
 * Computes the current scroll depth as a percentage. Same math as
 * `bootstrap/utilities/eventTracker/scrollDepthDecorator` (which does not export it):
 * scrollY / (scrollHeight − innerHeight) × 100, clamped, SSR-safe. Recomputed per scroll event so a
 * shrinking page height is reflected immediately.
 */
function getScrollDepthPercentage(): number | null {
	/* istanbul ignore next -- @preserve: SSR safety guard, unreachable in jsdom */
	if (typeof window === 'undefined' || typeof document === 'undefined') return null;

	const scrollableHeight = document.body.scrollHeight - window.innerHeight;
	if (scrollableHeight <= 0) return 100;

	const scrollY = Math.max(window.scrollY, 0);
	const clampedScrollY = Math.floor(Math.min(scrollY, scrollableHeight));

	return Math.round((clampedScrollY / scrollableHeight) * 100);
}

/**
 * Fires once when the viewer scrolls past `SCROLL_DEPTH_THRESHOLD_PERCENT` of the page while
 * `enabled`. Returns whether the crossing has happened. Detaches the listener after firing and
 * re-arms (forgets the crossing, re-attaches) whenever the route pathname changes.
 */
export function useScrollDepthTrigger({ enabled }: { enabled: boolean }): boolean {
	const { pathname } = useLocation();
	// Keyed to the page the crossing happened on, so it re-arms automatically when the pathname
	// changes (no reset effect needed).
	const [crossedPathname, setCrossedPathname] = useState<string | null>(null);
	const hasCrossed = crossedPathname === pathname;

	useEffect(() => {
		if (!enabled || hasCrossed) return undefined;

		const handleScroll = (): void => {
			const depth = getScrollDepthPercentage();
			if (depth !== null && depth >= SCROLL_DEPTH_THRESHOLD_PERCENT) {
				setCrossedPathname(pathname);
			}
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [enabled, hasCrossed, pathname]);

	return hasCrossed;
}
